/*
  Pre-Filled Gel Box template: the Isaac/Pascal/Watt/Joule/Kelvin model-details
  tab selector, defined as a Custom Element and loaded as a genuine external
  asset from sections/product-prefilled-gel-box-scripts.liquid -- the same
  safe location the original Gel Box Line candidate theme's own copy of this
  script uses, for the same reason: Shopify's theme editor re-renders
  whatever section a merchant is currently editing through the Section
  Rendering API (an innerHTML-style DOM operation, not a real HTML parse),
  and any <script> inserted that way is permanently inert per the HTML spec.
  This section has editable settings of its own, so it WOULD be subject to
  that re-render the moment someone edits it in the theme editor --
  registering the element from a script that never lives inside a
  re-rendered section avoids that regardless of which section triggers it.

  This component has no real Shopify form inputs to preserve and no native
  picker to defer to -- it's a plain ARIA tabs widget. All five panels are
  rendered in the DOM up front (only the first is visible by default) so
  every model's content is already present without JavaScript; this script
  only toggles which one is visible and wires up click/keyboard interaction.

  Exact copy of theme 165062803675's own
  assets/product-gel-box-line-model-details.js -- isolated here under its
  own filename so this template never depends on that theme's own copy, and
  so Ray Gel's product-universal.js is never touched.
*/
(function () {
  if (customElements.get('gbl-model-tabs')) return;

  // Mobile swipe-to-change-model. Identical in logic to
  // assets/product-universal.js's attachModelSwipeNavigation (duplicated,
  // not shared by reference, per this project's established per-template
  // isolation convention -- see that file's own comment on this function
  // for the full reasoning, including why prefers-reduced-motion isn't
  // branched on here). Host-agnostic: only takes the panel elements, how
  // to read the active index, and how to switch to a given index, so it
  // touches no Gel Box Line content, label, image, ordering, or styling.
  function attachModelSwipeNavigation(panels, getActiveIndex, goTo) {
    if (!panels.length) return;
    if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var THRESHOLD = 48;
    var LOCK_RATIO = 1.5;

    panels.forEach(function (panel) {
      var startX = 0;
      var startY = 0;
      var tracking = false;
      var locked = null;

      panel.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { tracking = false; return; }
        if (e.target.closest && e.target.closest('a, button, input, select, textarea, [data-no-swipe]')) {
          tracking = false;
          return;
        }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
        locked = null;
      }, { passive: true });

      panel.addEventListener('touchmove', function (e) {
        if (!tracking || e.touches.length !== 1) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (locked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          locked = Math.abs(dx) > Math.abs(dy) * LOCK_RATIO ? 'x' : 'y';
        }
        if (locked === 'x' && e.cancelable) e.preventDefault();
      }, { passive: false });

      panel.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        if (locked !== 'x') return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < THRESHOLD) return;
        var activeIndex = getActiveIndex();
        if (activeIndex === -1) return;
        if (dx < 0) {
          if (activeIndex < panels.length - 1) goTo(activeIndex + 1);
        } else {
          if (activeIndex > 0) goTo(activeIndex - 1);
        }
      }, { passive: true });

      panel.addEventListener('touchcancel', function () {
        tracking = false;
        locked = null;
      }, { passive: true });
    });
  }

  class GblModelTabsElement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.gblReady === 'true') return;

      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

      var self = this;
      this.tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          self.activate(index, false);
        });
        tab.addEventListener('keydown', function (event) {
          self.onKeydown(event, index);
        });
      });

      // This custom element is exclusive to Gel Box Line's own branch of
      // the shared template -- no opt-in marker is needed the way Happy's
      // data-uni-swipe is, since nothing else on this template ever
      // renders a <gbl-model-tabs> element for swipe to leak into.
      attachModelSwipeNavigation(
        this.panels,
        function getActiveIndex() {
          return self.tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
        },
        function goTo(index) {
          self.activate(index, false);
        }
      );

      this.dataset.gblReady = 'true';
    }

    onKeydown(event, index) {
      var key = event.key;
      var newIndex = null;
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        newIndex = (index + 1) % this.tabs.length;
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        newIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      } else if (key === 'Home') {
        newIndex = 0;
      } else if (key === 'End') {
        newIndex = this.tabs.length - 1;
      } else {
        return;
      }
      event.preventDefault();
      this.activate(newIndex, true);
    }

    activate(index, moveFocus) {
      this.tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });
      if (moveFocus) this.tabs[index].focus();
    }
  }

  customElements.define('gbl-model-tabs', GblModelTabsElement);
})();
